<template>
  <form
    class="flex w-full flex-col gap-6"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />

    <BaseInlineNotification
      v-if="submissionError"
      heading-text="An Error Occurred"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="submissionError"
      />
    </BaseInlineNotification>

    <BaseInlineNotification
      v-if="hasRecaptchaError"
      heading-text="reCAPTCHA Verification Failed"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="'Security verification failed. Please try again.'"
      />
    </BaseInlineNotification>

    <BaseTextInput
      id="firstName"
      v-model="values.firstName"
      autocomplete="given-name"
      helper-text-id="first-name-helper-text"
      label="First Name"
      maxlength="50"
      name="firstName"
      required
      validation-rules="required"
    >
      <template #helperText>
        <p>
          <span v-text="'Please use your professional license name, if applicable. You can look it up here: '" />
          <a
            class="wc-focus rounded-sm underline"
            href="https://www.healthguideusa.org"
            rel="noopener noreferrer"
            target="_blank"
            v-text="'www.healthguideusa.org'"
          />
        </p>
      </template>
    </BaseTextInput>
    
    <BaseTextInput
      id="middleName"
      v-model="values.middleName"
      autocomplete="additional-name"
      label="Middle Name"
      maxlength="50"
      name="middleName"
    />

    <BaseTextInput
      id="lastName"
      v-model="values.lastName"
      autocomplete="family-name"
      label="Last Name"
      maxlength="50"
      name="lastName"
      required
      validation-rules="required"
    />

    <BaseTextInput
      id="personalMobilePhone"
      v-model="values.personalMobilePhone"
      autocomplete="tel"
      label="Personal Phone"
      name="personalMobilePhone"
      required
      validation-rules="required"
    />

    <BaseTextInput
      id="email"
      v-model="values.email"
      autocomplete="email"
      label="Email"
      name="email"
      required
      type="email"
      validation-rules="required|email"
    />

    <BaseTextInput
      id="confirmEmail"
      v-model="values.confirmEmail"
      autocomplete="email"
      label="Confirm Email"
      name="confirmEmail"
      required
      type="email"
      validation-rules="required|confirmEmail:@email"
    />

    <div class="flex flex-col gap-4">
      <BasePasswordInput
        id="password"
        v-model="values.password"
        :error-message="errors.password"
        autocomplete="new-password"
        label="Password"
        name="password"
        required
      />

      <BasePasswordValidation
        :error="errors.password"
        :password="values.password"
        show-validation
      />
    </div>

    <BasePasswordInput
      id="confirmPassword"
      v-model="values.confirmPassword"
      :error-message="errors.confirmPassword"
      autocomplete="new-password"
      label="Confirm Password"
      name="confirmPassword"
      required
      validation-rules="required|confirmPassword:@password"
    />

    <BaseTextInput
      id="secondaryEmail"
      v-model="values.secondaryEmail"
      autocomplete="email"
      helper-text-id="alternate-email-helper-text"
      label="Alternate Email"
      name="secondaryEmail"
      type="email"
      validation-rules="email"
    >
      <template #helperText>
        <p v-text="'For password retrieval purposes only.'" />
      </template>
    </BaseTextInput>

    <p class="body-sm">
      <span v-text="`By creating an account, you indicate that you've read our `" />
      <a
        :href="utilityLinks.PRIVACY_POLICY"
        class="wc-focus rounded-sm underline"
        rel="noopener noreferrer"
        target="_blank"
        v-text="'Privacy Policy'"
      />
      <span v-text="' and agree to our '" />
      <a
        :href="utilityLinks.TERMS_CONDITIONS"
        class="wc-focus rounded-sm underline"
        v-text="'Terms of Use'"
      />
      <span v-text="'.'" />
    </p>

    <button
      type="submit"
      class="btn btn--primary"
      :disabled="isBusy"
      :aria-busy="isBusy"
      :aria-disabled="isBusy ? 'true' : 'false'"
    >
      <span class="relative inline-flex items-center justify-center">
        <BaseLoadingIcon v-if="isBusy" :visible="true" />
        <span
          class="whitespace-nowrap"
          :class="{ 'invisible': isBusy }"
        >
          Create an Account
        </span>
      </span>
    </button>

    <BaseRecaptchaNotice />
  </form>
</template>

<script lang="ts" setup>
import { useForm } from 'vee-validate'
import { onBeforeMount, onMounted, ref, computed, nextTick } from 'vue'
import { trackEvent } from '@js/utils/trackEvent'
import { log } from '@js/utils/logger'
import { createCustomer } from '@js/api/auth/register'
import { accountLinks, utilityLinks } from '@js/utils/links'

// import BaseButton from '@js/components/global/BaseButton.vue' // no longer used
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BasePasswordInput from '@js/components/global/forms/inputs/BasePasswordInput.vue'
import BasePasswordValidation from '@js/components/global/forms/BasePasswordValidation.vue'
import BaseRecaptchaNotice from '../global/forms/BaseRecaptchaNotice.vue'

import useAuth from '@js/composables/useAuth'
import useRecaptcha from '@js/composables/useRecaptcha'
import useCourseRegistration from '@js/composables/useCourseRegistration'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'
import { getReturnPath } from '@js/utils/returnPath'

defineOptions({
  name: 'AppRegisterForm',
})

const {
  values,
  errors,
  resetForm,
  submitCount,
  isSubmitting,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    firstName: '',
    middleName: '',
    lastName: '',
    personalMobilePhone: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    secondaryEmail: '',
  },
})

const { logInUser } = useAuth()
const { loadRecaptcha, getRecaptchaToken, validateRecaptcha, isValidatingRecaptcha } = useRecaptcha()

const {
  setSkuAndCourseOption,
  wasRedirectedFromCourseRegister,
  handleRedirectFromCourseRegister,
} = useCourseRegistration()

const submissionError = ref('')
const returnPath = ref<string | null>(null)
const hasRecaptchaError = ref(false)

// Single source of truth for "busy" state used by spinner and disabled state
const isBusy = computed(() => isSubmitting.value || isValidatingRecaptcha.value)

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  try {
    submissionError.value = ''
    hasRecaptchaError.value = false

    const token = await getRecaptchaToken('register')
    if (!token) {
      hasRecaptchaError.value = true
      return
    }

    await createCustomer({
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      personalMobilePhone: values.personalMobilePhone,
      email: values.email,
      password: values.password,
      secondaryEmail: values.secondaryEmail,
      recaptchaToken: token,
    })

    await logInUser({
      email: values.email,
      password: values.password,
    })

    // Track the registration event in GTM
    trackEvent({
      event: 'registration_accountcreated',
      method: 'email',
      user_email: values.email,
    })

    if (wasRedirectedFromCourseRegister.value) {
      await handleRedirectFromCourseRegister()
    } else {
      // Send the user back where they came from, e.g. an external webinar
      // registration page, otherwise fall back to the dashboard
      window.location.assign(returnPath.value ?? accountLinks.DASHBOARD)
    }
  } catch (error) {
    submissionError.value = 'An error occurred. Please try again.'
    log({
      message: 'Registration Error',
      file: 'src/wcei/js/components/auth/AppRegisterForm.vue',
    })
  } finally {
    resetForm()
  }
})

onBeforeMount(() => {
  setSkuAndCourseOption()

  returnPath.value = getReturnPath()
})

onMounted(() => {
  loadRecaptcha()
})
</script>
